import { z } from 'zod';

/** Trimmed, lower-cased email. The database also enforces lower case. */
export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .max(254, 'Email is too long.')
  .pipe(z.email('Enter a valid email address.'));

export const PASSWORD_MIN = 10;
export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN, `Use at least ${PASSWORD_MIN} characters.`)
  .max(128, 'Use at most 128 characters.');

/** ISO-3166-1 alpha-2 codes offered at signup. Anything else can be added later. */
export const COUNTRIES = [
  'IN',
  'US',
  'GB',
  'CA',
  'AU',
  'NZ',
  'IE',
  'DE',
  'FR',
  'NL',
  'ES',
  'IT',
  'SE',
  'DK',
  'NO',
  'FI',
  'BE',
  'AT',
  'CH',
  'PT',
  'PL',
  'AE',
  'SA',
  'SG',
  'MY',
  'ID',
  'PH',
  'TH',
  'JP',
  'KR',
  'HK',
  'ZA',
  'NG',
  'KE',
  'BR',
  'MX',
  'AR',
  'CL',
  'BD',
  'LK',
  'NP',
  'PK',
] as const;
export const countrySchema = z.enum(COUNTRIES, { message: 'Choose a country.' });

export const registerSchema = z.object({
  name: z.string().trim().min(1, 'Enter your name.').max(80),
  email: emailSchema,
  password: passwordSchema,
  storeName: z.string().trim().min(2, 'Enter your store name.').max(80),
  country: countrySchema,
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Enter your password.').max(128),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const forgotPasswordSchema = z.object({ email: emailSchema });
export const tokenSchema = z.object({ token: z.string().min(20).max(200) });
export const resetPasswordSchema = z.object({
  token: z.string().min(20).max(200),
  password: passwordSchema,
});
export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1, 'Enter your current password.').max(128),
  newPassword: passwordSchema,
});
export const updateProfileSchema = z.object({ name: z.string().trim().min(1).max(80) });

export const updateMerchantSchema = z.object({
  name: z.string().trim().min(2).max(80).optional(),
  timezone: z
    .string()
    .max(64)
    .refine((tz) => {
      try {
        new Intl.DateTimeFormat('en', { timeZone: tz });
        return true;
      } catch {
        return false;
      }
    }, 'Unknown time zone.')
    .optional(),
});

export interface Me {
  user: {
    id: string;
    email: string;
    name: string | null;
    emailVerified: boolean;
    isPlatformAdmin: boolean;
  };
  merchant: {
    id: string;
    name: string;
    slug: string;
    country: string;
    timezone: string;
    defaultCurrency: string;
    billingChannel: 'DIRECT' | 'SHOPIFY';
  } | null;
  role: 'OWNER' | 'ADMIN' | 'MEMBER' | null;
  csrfToken: string;
  /** Set when TrackFlow is open inside Shopify admin (authenticated by a Shopify ID token). */
  shopify: { shop: string } | null;
}
